/**
 * Shared numeric helpers for the Almikia indicator components
 * (ScoreRing, ProgressBar, MonthBar). Kept dependency-free and pure so every
 * component can be exercised with plain unit assertions on the resulting
 * numbers, in addition to the rendered DOM.
 */

/**
 * Clamps `value` to the closed range [min, max]. Non-finite values
 * (NaN, +Infinity, -Infinity, undefined-coerced-to-NaN) fall back to `min`,
 * matching the "values below 0 or non-finite render as 0" rule shared by
 * every indicator in this file.
 */
export function clampToRange(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

/**
 * Clamps a raw value/max pair to [0, max] and returns both the clamped value
 * and the resulting fraction (0-1) of max. `max` itself is defended against
 * a non-positive/non-finite caller mistake by falling back to 1.
 */
export function clampToMax(
  value: number,
  max: number,
): { clamped: number; fraction: number } {
  const safeMax = Number.isFinite(max) && max > 0 ? max : 1;
  const clamped = clampToRange(value, 0, safeMax);
  return { clamped, fraction: clamped / safeMax };
}
